import classNames from 'classnames'
import {Formik} from 'formik'
import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {ClockIcon, HelpIcon, InfoIcon, SecondaryTrashIcon} from '../../../icons'
import {colors} from '../../../utils/colors'
import {extractOnlyValues} from '../../../utils/forms'
import regexPatterns from '../../../utils/regexPatterns'
import {addDynamicRulesetSchema} from '../../../utils/validation'
import {SimpleBlueButton} from '../../core/button'
import ButtonLoader from '../../core/button/ButtonLoader'
import CustomSelect from '../../core/customSelect'
import Input from '../../core/input'
import './index.scss'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import {useDispatch, useSelector} from 'react-redux'
import Tooltip from '../../core/tooltip'
import {getRulesetMetricKeys} from '../../../stores/server-monitoring'
import {categoryHasComponents, getMetricComponentOptions} from '../../../utils/rulesetMetrics'

const applyDisabledKeyOptions = (pairs) => {
    const selectedKeyValues = pairs
        .map(c => c.key?.value?.value)
        .filter(Boolean)

    return pairs.map(condition => ({
        ...condition,
        key: {
            ...condition.key,
            options: (condition.key?.options ?? []).map(opt => ({
                ...opt,
                isDisabled: selectedKeyValues.includes(opt.value)
                    && opt.value !== condition.key?.value?.value
            }))
        }
    }))
}

const buildComponentField = (metricOption, existingValue, serverMonitoringState, serverMetrics) => {
    const metricCategory = metricOption?.category
    if (!metricOption?.is_setup_required || !metricCategory || !categoryHasComponents(metricCategory)) return null

    const options = getMetricComponentOptions(metricCategory, serverMonitoringState, serverMetrics)
    if (!options.length) return null

    return {
        options,
        value: options.find(option => option.value === existingValue) ?? null
    }
}

const getConditionErrors = (errors, index) => {
    const pairErrors = errors?.conditions?.pairs?.[index]

    if (!pairErrors) return null

    const messages = Object.entries(pairErrors)
        .map(([, fieldError]) => fieldError?.value)
        .filter(Boolean)

    return messages.length > 0 ? messages : null
}

const RulesetForm = ({
    rulesetFormInitialValues,
    rulesetTypeOptions,
    setRulesetValues,
    closeModal,
    existingComponent,
    existingRuleset,
    ...props
}) => {
    const isDark = useSelector(state => state.sidebar.isDarkMode)
    const serverMonitoringState = useSelector(state => state.server_monitoring)
    const text = useNamespaceTranslations('rulesets')
    const serverMetrics = useNamespaceTranslations('server-metrics')?.server_metrics
    const jsonType = useNamespaceTranslations('condition-types')?.resource_conditions?.json_key_value_check
    const conditionTypeOptions = useMemo(() => Object.values(jsonType?.values), [jsonType])
    const [isButtonLoading, setIsButtonLoading] = useState(false)
    const [conditions, setConditions] = useState(() => initializeConditions(
        rulesetFormInitialValues,
        props.isCronJobRuleset,
        rulesetTypeOptions,
        jsonType,
        conditionTypeOptions
    ))
    const [componentField, setComponentField] = useState(() => !props.isCronJobRuleset && buildComponentField(
        rulesetFormInitialValues?.type?.value,
        existingComponent,
        serverMonitoringState,
        serverMetrics
    ))

    const formikRef = useRef()
    const dispatch = useDispatch()

    const typeField = useMemo(() => {
        const field = rulesetFormInitialValues?.type
        if (!field || props.isCronJobRuleset) return field

        let hasUnavailableComponentOption = false
        const options = (field.options ?? []).map(option => {
            if (option.isDisabled) return option

            const needsUnavailableComponent = !!option.is_setup_required &&
                categoryHasComponents(option.category) &&
                !getMetricComponentOptions(option.category, serverMonitoringState, serverMetrics).length

            if (!needsUnavailableComponent) return option
            hasUnavailableComponentOption = true
            return {...option, isDisabled: true}
        })

        return {
            ...field,
            options,
            below_text: field.below_text || (hasUnavailableComponentOption ? text.component_data_unavailable : undefined)
        }
    }, [rulesetFormInitialValues?.type, serverMonitoringState, serverMetrics, props.isCronJobRuleset, text.component_data_unavailable])

    useEffect(() => {
        if (props.isCronJobRuleset) return
        const selectedValue = formikRef.current?.values?.type?.value?.value
        if (!selectedValue) return
        const selectedOption = props.metricOptions?.find(option => option.value === selectedValue)
        if (!selectedOption) return

        setComponentField(prev => buildComponentField(
            selectedOption,
            prev?.value?.value ?? existingComponent,
            serverMonitoringState,
            serverMetrics
        ))
    }, [serverMonitoringState, serverMetrics, props.isCronJobRuleset, props.metricOptions, existingComponent])

    const defaultCondition = useMemo(() => rulesetFormInitialValues?.conditions?.pairs?.[0], [rulesetFormInitialValues])

    const getConditionOperatorOptions = useCallback((typeValue) => {
        if (!props.isCronJobRuleset) return conditionTypeOptions

        if (typeValue === rulesetTypeOptions?.result?.value) {
            return conditionTypeOptions.filter(opt => opt.value === jsonType.values.equal.value)
        }

        if (typeValue === rulesetTypeOptions?.message?.value) {
            return conditionTypeOptions.filter(opt => [
                jsonType.values.contains.value,
                jsonType.values.not_contains.value
            ].includes(opt.value))
        }

        return conditionTypeOptions
    }, [props.isCronJobRuleset])

    const addRuleset = async(values) => {
        const updatedType = props.metricOptions?.find(type => type.value === values.type?.value?.value)
        const filteredConditions = values.conditions.pairs.filter((c, index) => {
            const hasValue = c.values?.value !== '' && c.values?.value !== null && c.values?.value !== undefined
            return index === 0 || hasValue
        })
        const existingIndex = existingRuleset ? serverMonitoringState.rulesets.indexOf(existingRuleset) : -1

        setRulesetValues({
            ruleset: {
                ...values,
                type: updatedType,
                component: componentField ? values.component?.value ?? null : null,
                conditions: {...values.conditions, pairs: filteredConditions}
            },
            index: existingIndex
        })

        setIsButtonLoading(false)
        closeModal()
    }

    const updateConditions = useCallback((conditionType, setFieldValue, values, errors, setErrors) => {
        if (!conditionType) return

        if (!props?.isCronJobRuleset) {
            setComponentField(buildComponentField(conditionType, null, serverMonitoringState, serverMetrics))
            setFieldValue('component', {value: null})
        }

        const isResult = conditionType.value === rulesetTypeOptions?.result?.value
        const isMessage = conditionType.value === rulesetTypeOptions?.message?.value

        const metricConditionOptions = conditionType?.charts
            ? Object.values(conditionType.charts).reduce((options, chart) => {
                if (chart?.condition?.options) {
                    options.push(...Object.values(chart.condition.options))
                }
                return options
            }, [])
            : []

        const resultOptions = conditionType?.condition?.options

        if (isResult && resultOptions) {
            metricConditionOptions.push(...Object.values(resultOptions))
        }

        const defaultKeyValue = props?.isCronJobRuleset && !isResult
            ? rulesetTypeOptions?.[conditionType.value]
            : metricConditionOptions[0] ?? null

        const updatedKey = {
            ...defaultCondition.key,
            value: defaultKeyValue,
            options: props?.isCronJobRuleset
                ? [defaultKeyValue]
                : metricConditionOptions
        }

        const updatedValues = {
            ...defaultCondition.values,
            value: isResult ? defaultKeyValue : '',
            options: metricConditionOptions,
            FormItem: isResult ? CustomSelect : Input,
            type: isMessage ? 'text' : defaultCondition?.values?.type,
            form_type: isMessage ? 'input' : defaultCondition?.values?.form_type
        }

        const newConditionPair = {
            key: updatedKey,
            operator: {
                ...defaultCondition.operator,
                options: getConditionOperatorOptions(conditionType.value),
                value: getConditionOperatorOptions(conditionType.value)[0]
            },
            values: updatedValues,
            ...defaultCondition.last_time && {last_time: defaultCondition.last_time}
        }

        setConditions({...values.conditions, pairs: applyDisabledKeyOptions([newConditionPair])})
        setFieldValue('conditions', {...values.conditions, pairs: extractOnlyValues([newConditionPair])})
        if (errors?.conditions?.pairs) setErrors({...errors, conditions: {}})
    }, [defaultCondition, getConditionOperatorOptions, serverMonitoringState, serverMetrics])

    const handleRemoveConditions = useCallback((index, setFieldValue, values, errors, setErrors) => {
        const updatedPairs = conditions.pairs.filter((_, i) => i !== index)
        const pairsWithDisabledKeys = applyDisabledKeyOptions(updatedPairs)
        setConditions({...conditions, pairs: pairsWithDisabledKeys})
        setFieldValue('conditions', {...values.conditions, pairs: extractOnlyValues(updatedPairs)})

        if (errors?.conditions?.pairs) {
            setErrors({
                ...errors,
                conditions: {
                    pairs: errors.conditions.pairs.filter((_, i) => i !== index)
                }
            })
        }
    }, [conditions])

    const handleConditionChange = useCallback((index, formItem, value, e, setFieldValue, values) => {
        const updatedConditions = [...conditions.pairs]
        const newValue = value || e?.target?.value || null
        const typeValue = values?.type?.value?.value

        let operatorOptions = getConditionOperatorOptions(typeValue)
        if (props.isCronJobRuleset && regexPatterns.digitsOnly.test(newValue)) {
            operatorOptions = conditionTypeOptions
        }

        updatedConditions[index] = {
            ...updatedConditions[index],
            [formItem.name]: {
                ...updatedConditions[index][formItem.name],
                value: newValue
            },
            ...formItem.name !== 'operator' && {
                operator: {
                    ...updatedConditions[index].operator,
                    options: operatorOptions
                }
            }
        }

        const currentKeyOptions = conditions.pairs[0]?.key?.options ?? []
        const selectedKeyValues = updatedConditions
            .map(c => c.key?.value?.value)
            .filter(Boolean)
        const allOptionsUsed = currentKeyOptions.length > 0
            && selectedKeyValues.length >= currentKeyOptions.length

        const shouldAddNew = index === updatedConditions.length - 1
            && typeValue !== rulesetTypeOptions?.result?.value
            && !allOptionsUsed

        if (shouldAddNew) {
            updatedConditions.push({
                ...defaultCondition,
                key: {
                    ...defaultCondition.key,
                    options: props?.isCronJobRuleset
                        ? [rulesetTypeOptions?.[typeValue]]
                        : currentKeyOptions,
                    value: null
                },
                operator: {
                    ...defaultCondition.operator,
                    options: getConditionOperatorOptions(typeValue),
                    value: getConditionOperatorOptions(typeValue)[0]
                },
                values: {
                    ...defaultCondition.values,
                    value: '',
                    FormItem: typeValue === rulesetTypeOptions?.result?.value ? CustomSelect : Input,
                    form_type: typeValue === rulesetTypeOptions?.result?.value ? 'select' : 'input'
                }
            })
        }

        setConditions({...conditions, pairs: applyDisabledKeyOptions(updatedConditions)})
        setFieldValue('conditions', {...values.conditions, pairs: extractOnlyValues(updatedConditions)})
    }, [conditions, rulesetTypeOptions, defaultCondition, getConditionOperatorOptions])

    const initialValues = useMemo(() => ({
        ...extractOnlyValues(rulesetFormInitialValues),
        component: {value: componentField?.value ?? null},
        conditions: {
            pairs:  extractOnlyValues(rulesetFormInitialValues.conditions.pairs)
        }
    }), [rulesetFormInitialValues])

    const getConditionFieldKeys = (condition) => {
        const keys = [condition.operator.name]
        keys.push(condition.values.name)
        return keys
    }

    useEffect(() => {
        if (props.isCronJobRuleset) return
        dispatch(getRulesetMetricKeys())
    }, [props.isCronJobRuleset, dispatch])

    return (
        <Formik
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
            innerRef={formikRef}
            validationSchema={() => addDynamicRulesetSchema(formikRef?.current?.values)}
            initialValues={initialValues}
            onSubmit={addRuleset}
        >
            {({values, handleSubmit, setFieldValue, errors, setErrors}) => (
                <form onSubmit={handleSubmit} className="rulesets-form-container form-container">
                    {Object.values(rulesetFormInitialValues).filter(field => field?.name !== 'message').map((rawField) => {
                        const field = rawField?.name === 'type' ? typeField : rawField
                        return field?.FormItem
                            ? (
                                <Fragment key={field.name}>
                                    {renderFormItem(field, values, setFieldValue, errors, updateConditions, setErrors, isDark, text.setup_required)}
                                    {field.name === 'type' && componentField && renderFormItem({
                                        name: 'component',
                                        label: text.component,
                                        form_type: 'select',
                                        info_text: text.component_info_text,
                                        FormItem: CustomSelect,
                                        placeholder: text.select_component_placeholder,
                                        options: componentField.options
                                    }, values, setFieldValue, errors, updateConditions, setErrors, isDark)}
                                </Fragment>
                            )
                            : conditions.pairs.map((condition, index) => {
                                const conditionErrors = getConditionErrors(errors, index)

                                return (
                                    <div key={index} className="condition-pair-wrapper">
                                        <div className={classNames('child-form-items', {
                                            dark: isDark,
                                            'has-error': conditionErrors
                                        })}>
                                            <div className="condition-section">
                                                <span className={classNames('condition-section-label', {dark: isDark})}>
                                                    {text.condition}
                                                </span>
                                                <div className="condition-fields-row">
                                                    {getConditionFieldKeys(condition).map((formItemKey) => {
                                                        const formItem = condition[formItemKey]
                                                        if (!formItem) return null
                                                        return renderChildFormItem({
                                                            formItemKey,
                                                            formItem,
                                                            index,
                                                            rulesetFormInitialValues,
                                                            errors,
                                                            handleRemoveConditions,
                                                            handleConditionChange,
                                                            setFieldValue,
                                                            values,
                                                            setErrors
                                                        })
                                                    })}
                                                </div>
                                            </div>

                                            {condition?.last_time && (
                                                <>
                                                    <div className={classNames('condition-divider', {dark: isDark})} />
                                                    <div className="condition-section">
                                                        <span className={classNames('condition-section-label', {dark: isDark})}>
                                                            {text.time_window}
                                                        </span>
                                                        <div className={classNames('condition-time-window', {dark: isDark})}>
                                                            <ClockIcon
                                                                width={18}
                                                                height={18}
                                                                stroke={isDark ? colors.colorBgFilter : colors.colorPrimary}
                                                            />
                                                            <span className="time-window-text">
                                                                {text.trigger_condition_holds}
                                                            </span>
                                                            <input
                                                                name="last_time"
                                                                type="number"
                                                                placeholder="1"
                                                                value={condition.last_time.value}
                                                                className="time-window-input"
                                                                onChange={(e) => handleConditionChange(
                                                                    index,
                                                                    condition.last_time,
                                                                    null,
                                                                    e,
                                                                    setFieldValue,
                                                                    values
                                                                )}
                                                            />
                                                            <span className="time-window-unit">{text.minutes || 'minutes'}</span>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {conditionErrors && (
                                                <div className="condition-errors">
                                                    {conditionErrors.map((msg, i) => (
                                                        <span key={i} className="condition-error-message">{msg}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {index !== 0
                                            ? (
                                                <button
                                                    type="button"
                                                    className="condition-delete-button"
                                                    onClick={() => handleRemoveConditions(index, setFieldValue, values, errors, setErrors)}
                                                >
                                                    <SecondaryTrashIcon stroke={colors.colorRed} />
                                                </button>
                                            )
                                            : <Tooltip content={<span dangerouslySetInnerHTML={{__html: text.json_info}}/>} placement="top">
                                                <HelpIcon
                                                    width={22}
                                                    height={22}
                                                    stroke={isDark ? colors.colorBgFilter : colors.colorFilterTitle}
                                                    className="condition-info-icon"
                                                />
                                            </Tooltip>
                                        }
                                    </div>
                                )
                            })
                    })}
                    <div className={classNames('form-actions', {'has-cancel-button': props?.hasCancelButton})}>
                        <SimpleBlueButton type="submit" className="form-action-submit">
                            {isButtonLoading && <ButtonLoader />}
                            <span>{text.save}</span>
                        </SimpleBlueButton>
                    </div>
                </form>
            )}
        </Formik>
    )
}

const initializeConditions = (initialValues, isCronJobRuleset, rulesetTypeOptions, jsonType, conditionTypeOptions) => ({
    ...initialValues?.conditions,
    pairs: [
        {
            ...initialValues?.conditions?.pairs?.[0],
            ...initialValues?.conditions?.pairs?.[0]?.value_type && {value_type:
                initialValues?.conditions?.pairs?.[0]?.value_type
            },
            operator: {
                ...initialValues?.conditions?.pairs?.[0]?.operator,
                options: initializeConditionOptions(
                    initialValues?.type?.value?.value,
                    isCronJobRuleset,
                    rulesetTypeOptions,
                    jsonType,
                    conditionTypeOptions
                )
            },
            ...initialValues?.conditions?.pairs?.[0]?.last_time && {last_time:
                initialValues?.conditions?.pairs?.[0]?.last_time
            }
        }
    ]
})

const initializeConditionOptions = (typeValue, isCronJobRuleset, rulesetTypeOptions, jsonType, conditionTypeOptions) => {
    if (!isCronJobRuleset) return conditionTypeOptions

    if (typeValue === rulesetTypeOptions?.result?.value) {
        return conditionTypeOptions.filter(opt => opt.value === jsonType.values.equal.value)
    }

    if (typeValue === rulesetTypeOptions?.message?.value) {
        return conditionTypeOptions.filter(opt => [
            jsonType.values.contains.value,
            jsonType.values.not_contains.value
        ].includes(opt.value))
    }

    return conditionTypeOptions
}

const renderFormItem = (field, values, setFieldValue, errors, updateConditions, setErrors, isDark, disabledOptionTagText) => {
    const {FormItem, name, options, placeholder, info_text: infoText, below_text: belowText, label, type, form_type: formType} = field
    if (!FormItem) return null

    return (
        <div key={name}>
            <FormItem
                options={options}
                name={name}
                placeholder={placeholder}
                value={values[name]?.value}
                infoText={infoText}
                error={errors?.[name]?.value}
                label={label}
                checked={values[name].value}
                type={type || 'text'}
                disabledOptionTagText={disabledOptionTagText}
                isOptionDisabled={(option) => option.isDisabled}
                onChange={(e, value) => {
                    const updatedValue = formType === 'checkbox'
                        ? {...values[name], value: !values[name].value}
                        : formType === 'select'
                            ? {value: {value: value.value, label: value.label}, type: value.type}
                            : {...values[name], value: e.target.value || ''}

                    setFieldValue(name, updatedValue)
                    if (name === 'type') updateConditions(value, setFieldValue, values, errors, setErrors)
                }}
            />
            {belowText && (
                <div className={classNames('form-field-below-text', {dark: isDark})}>
                    <InfoIcon color="#2b63e3" width={14} height={14} />
                    <span>{belowText}</span>
                </div>
            )}
        </div>
    )
}

const renderChildFormItem = ({
    formItemKey,
    formItem,
    index,
    rulesetFormInitialValues,
    handleConditionChange,
    setFieldValue,
    values
}) => {
    const ChildFormItem = formItem.FormItem
    if (!ChildFormItem) return null

    return (
        <ChildFormItem
            key={formItemKey}
            inputIsDisabled={formItemKey === 'key' && rulesetFormInitialValues.type.value.isInputDisabled}
            options={formItem.options}
            name={formItem.name}
            placeholder={formItem.placeholder}
            value={formItem.value}
            label={formItem.label}
            type={formItem.type || 'text'}
            hasLabel={formItem.hasLabel}
            isOptionDisabled={(option) => option.isDisabled}
            onChange={(e, value) => handleConditionChange(index, formItem, value, e, setFieldValue, values)}
        />
    )
}

export default RulesetForm
